import {
  getOrderTrackingStatusName,
  getOrderTrackingUrl,
  ORDER_COURIERS,
  type OrderShipment,
} from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { OrderFlagBadge } from '@/entities/order';
import { OrderShipmentActions } from '@/features/order/shipping';
import { formatDateTime, type OrderActions } from '@/shared/lib';
import { detailListStyles as styles } from '@/shared/ui';

interface OrderShipmentRowProps {
  readonly shipment: OrderShipment;
  readonly count: number;
  readonly active: boolean;
  readonly now: string;
  readonly actions: OrderActions;
  readonly onReload: () => void;
  readonly pending?: boolean;
}

/** 택배 추적 등록 뒤 추적이 끝나는 기간(14일). */
const TRACKING_EXPIRY_MS = 14 * 24 * 60 * 60 * 1000;
const RETURNED_STATUSES = ['RETURNED', 'CANCELLED'];

/** 추적 상태 화면 이름 — 결과가 없으면 조회 대기, 이름 표에 없는 코드는 운영자가 확인하도록 원문 그대로. */
const trackingStatusLabel = (code: string | null): string =>
  code === null ? '조회 대기' : (getOrderTrackingStatusName(code) ?? code);

/** 송장 한 건 — 택배(택배사·송장 번호·추적 상태·진행 내역) 또는 직접 전달(방법 메모), 배송 중이면 삭제·다시 추적. */
export const OrderShipmentRow = ({
  shipment,
  count,
  active,
  now,
  actions,
  onReload,
  pending,
}: OrderShipmentRowProps): ReactElement => {
  const isParcel = shipment.kind === 'PARCEL';
  const expired =
    isParcel &&
    !shipment.deliveredAt &&
    shipment.trackingRegisteredAt !== null &&
    new Date(now).getTime() - new Date(shipment.trackingRegisteredAt).getTime() >=
      TRACKING_EXPIRY_MS;
  const returned = isParcel && RETURNED_STATUSES.includes(shipment.trackingStatus ?? '');
  const retry =
    isParcel && !shipment.deliveredAt && (Boolean(shipment.trackingRegisterError) || expired);
  const url = isParcel ? getOrderTrackingUrl(shipment.courierCode, shipment.trackingNumber) : null;
  const courierName =
    ORDER_COURIERS.find((item) => item.code === shipment.courierCode)?.name ?? shipment.courierCode;

  return (
    <li className="rounded-xl border border-foreground/[0.08] p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className={styles.subheading}>
          {isParcel
            ? `${courierName} ${shipment.trackingNumber}`
            : `직접 전달 · ${shipment.directMethodNote}`}
        </h3>
        <span className="flex flex-wrap items-center gap-1.5">
          {expired ? <OrderFlagBadge badge="TRACKING_EXPIRED" /> : null}
          {returned ? <OrderFlagBadge badge="RETURNED" /> : null}
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium underline decoration-foreground/30 underline-offset-4 hover:decoration-foreground"
            >
              택배사 조회
            </a>
          ) : null}
        </span>
      </div>

      <dl className={`mt-3 ${styles.list}`}>
        <dt className={styles.term}>등록</dt>
        <dd>
          {formatDateTime(shipment.createdAt)} · 등록한 사람 {shipment.createdByName}
        </dd>
        {isParcel ? (
          <>
            <dt className={styles.term}>현재 상태</dt>
            <dd>{trackingStatusLabel(shipment.trackingStatus)}</dd>
            {shipment.trackingRegisteredAt ? (
              <>
                <dt className={styles.term}>추적 등록</dt>
                <dd>{formatDateTime(shipment.trackingRegisteredAt)}</dd>
              </>
            ) : null}
            {shipment.trackingCheckedAt ? (
              <>
                <dt className={styles.term}>최근 조회</dt>
                <dd>{formatDateTime(shipment.trackingCheckedAt)}</dd>
              </>
            ) : null}
            {shipment.trackingRegisterError ? (
              <>
                <dt className={styles.term}>추적 등록 실패</dt>
                <dd className="text-destructive">{shipment.trackingRegisterError}</dd>
              </>
            ) : null}
          </>
        ) : null}
        {shipment.deliveredAt ? (
          <>
            <dt className={styles.term}>배송 완료</dt>
            <dd>{formatDateTime(shipment.deliveredAt)}</dd>
          </>
        ) : null}
      </dl>

      {isParcel && shipment.trackingProgresses && shipment.trackingProgresses.length > 0 ? (
        <ol className="mt-3 flex flex-col gap-1 border-l-2 border-foreground/10 pl-3 text-xs text-muted-foreground">
          {shipment.trackingProgresses.map((progress) => (
            <li
              key={`${progress.dateTime}-${progress.statusCode}-${progress.location}-${progress.description}`}
            >
              {formatDateTime(progress.dateTime)} · {progress.location} · {progress.status} (
              {progress.statusCode}) · {progress.description}
            </li>
          ))}
        </ol>
      ) : null}

      {active ? (
        <OrderShipmentActions
          shipmentId={shipment.id}
          canRemove={count > 1 && !shipment.deliveredAt}
          canRetry={retry}
          onRemove={actions.removeShipment}
          onRetry={actions.retryTracking}
          onReload={onReload}
          pending={pending}
        />
      ) : null}
    </li>
  );
};
